import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { AGENDA_STATE_OPEN } from 'lib/agenda'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import { TABLE_WIDTH_PARTNER } from 'lib/tableLib'
import { orderBy } from 'lodash'
import T from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import ButtonLink from 'components/atoms/ButtonLink'
import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import FilterSelect from 'components/atoms/FilterSelect'
import PageLoading from 'components/atoms/PageLoading'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import TitleBar from 'components/atoms/TitleBar'

import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useSort from 'hooks/useSort'

import {
  getSessionsStepUrl,
  SESSIONS_STEP_PLANNING,
} from '../Sessions/sessionsLib'
import CapField from '../SessionsCaps/CapField'

import usePublicationFilters from './usePublicationFilters'

const getFirstDefinedSessionValue = (session, fieldNames) => {
  for (const fieldName of fieldNames) {
    const value = session?.[fieldName]

    if (value !== undefined && value !== null) {
      return value
    }
  }

  return undefined
}

const BLIND_LEGACY_COUNTER_FIELDS = [
  'blindCarsWithAtLeasOneChoice',
  'blindCarsWithAtLeastOneChoice',
  'carsWithAtLeasOneBlindOffer',
  'carsWithAtLeastOneBlindOffer',
  'carsWithAtLeasOneBlindChoice',
  'carsWithAtLeastOneBlindChoice',
]

const BLIND_POSITIVE_COUNTER_FIELDS = [
  'blindPositiveCarsWithAtLeasOneChoice',
  'blindPositiveCarsWithAtLeastOneChoice',
  'blindPositiveCarsWithAtLeasOneOffer',
  'blindPositiveCarsWithAtLeastOneOffer',
  'carsWithAtLeasOneBlindPositiveOffer',
  'carsWithAtLeastOneBlindPositiveOffer',
  'carsWithAtLeasOneBlindPositiveChoice',
  'carsWithAtLeastOneBlindPositiveChoice',
  'carsWithAtLeasOneBlindPlusOffer',
  'carsWithAtLeastOneBlindPlusOffer',
  'carsWithAtLeasOneBlindPlusChoice',
  'carsWithAtLeastOneBlindPlusChoice',
]

const BLIND_NEGATIVE_COUNTER_FIELDS = [
  'blindNegativeCarsWithAtLeasOneChoice',
  'blindNegativeCarsWithAtLeastOneChoice',
  'blindNegativeCarsWithAtLeasOneOffer',
  'blindNegativeCarsWithAtLeastOneOffer',
  'carsWithAtLeasOneBlindNegativeOffer',
  'carsWithAtLeastOneBlindNegativeOffer',
  'carsWithAtLeasOneBlindNegativeChoice',
  'carsWithAtLeastOneBlindNegativeChoice',
  'carsWithAtLeasOneBlindMinusOffer',
  'carsWithAtLeastOneBlindMinusOffer',
  'carsWithAtLeasOneBlindMinusChoice',
  'carsWithAtLeastOneBlindMinusChoice',
]

const createColumns = (
  partners,
  session,
  costErrorNumber,
  capErrorNumber,
  handleCapChange,
  readOnly,
  contentIsReadOnly,
  allOption,
  publicationFilters,
  t
) => {
  const { filters, filtersData, setPartners, setPartnerGroup } =
    publicationFilters

  return [
    {
      accessor: 'partnerGroup',
      Header: (
        <TableHeaderCell
          label={t('assignment.publication.table.partnerGroup')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.partnersGroup}
              value={filters.partnersGroup}
              handleChange={setPartnerGroup}
              multiple
              hasSearchField
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_PARTNER,
        },
      },
    },
    {
      accessor: 'name',
      Header: (
        <TableHeaderCell
          label={t('assignment.publication.table.partner')}
          filter={
            <FilterSelect
              allOption={allOption}
              options={filtersData.partners}
              value={filters.partners}
              handleChange={setPartners}
              multiple
              hasSearchField
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_PARTNER,
        },
      },
    },
    {
      accessor: 'capField',
      Header: (
        <TableHeaderCell
          label={t('assignment.publication.table.cap')}
          errorsNumber={capErrorNumber}
        />
      ),
      cellProps: {
        sx: {
          width: 80,
        },
      },
    },
    {
      accessor: 'cost',
      Header: (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TableHeaderCell
            label={t('assignment.publication.table.cost')}
            errorsNumber={costErrorNumber}
          />
          {!!costErrorNumber && (
            <Stack direction="row" alignItems="center">
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                sx={{ pr: 0.5 }}
              >
                <strong>
                  {t('assignment.publication.partnerWithoutCost')}
                </strong>
              </Typography>
              <Typography component="span" variant="body2" color="error">
                <strong>{costErrorNumber}</strong>
              </Typography>
              <Box pr={2} />
              <Button
                component={Link}
                to={`/rules/${session.sessionId}?type=costing`}
                size="small"
                variant="contained"
                color="primary"
                disabled={contentIsReadOnly}
              >
                {t('assignment.publication.closePublication')}
              </Button>
            </Stack>
          )}
        </Stack>
      ),
    },
  ]
}

const GridWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  columnGap: theme.spacing(3),
  paddingBottom: theme.spacing(1),
}))

export default function AssignmentPublication({
  session,
  partners,
  handleValidation,
  handleStartAssignment,
  handleCapChange,
  isFetchingPartners,
  readOnly,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const [filteredItems, setFilteredItems] = useState([])

  const navigate = useNavigate()

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleStartAssignment)

  const publicationFilters = usePublicationFilters()

  const { filters, initFilters, filter } = publicationFilters

  const { order, setSort } = useSort('partnerGroup')

  const handleFilters = useDebouncedCallback(
    (items, filters, setFilteredItems, filter) =>
      setFilteredItems(filter(items, filters)),

    100
  )

  useEffect(() => {
    if (!partners.length || filters?.initialized) {
      return
    }
    initFilters(partners)
  }, [initFilters, filters?.initialized, partners.length, partners])

  useEffect(() => {
    handleFilters(partners, filters, setFilteredItems, filter)
  }, [filter, filters, handleFilters, partners])

  const costErrorNumber = useMemo(
    () =>
      partners.filter((partner) => !partner.cost && partner.cost !== 0).length,
    [partners]
  )

  const capErrorNumber = useMemo(
    // cap === 0 is a valid explicit value (e.g. partner has no residual in
    // repair, Arval may keep it at 0). Only null/undefined/'' should count
    // as missing. Mirrors the pattern used for costErrorNumber above.
    () =>
      partners.filter((partner) => !partner.cap && partner.cap !== 0).length,
    [partners]
  )

  const preparedItems = useMemo(
    () =>
      filteredItems.map((partner) => {
        return {
          ...partner,
          capField: (
            <CapField
              value={partner.cap && Number(partner.cap)}
              id={`${partner.partnerId}.cap`}
              handleCapChange={(e) =>
                handleCapChange(partner.partnerId, e.target.value)
              }
              InputProps={null}
              sx={{ width: '8ch' }}
              canBeEmpty
              readOnly={
                session.agenda.assignment.state !== AGENDA_STATE_OPEN ||
                !!session?.assignmentStartedAt ||
                readOnly
              }
              disabled={contentIsReadOnly}
              placeholder=""
            />
          ),
        }
      }),
    [
      filteredItems,
      handleCapChange,
      readOnly,
      contentIsReadOnly,
      session.agenda.assignment.state,
      session?.assignmentStartedAt,
    ]
  )

  const sortedPartners = useMemo(
    () =>
      orderBy(
        preparedItems,
        [order?.column === 'capField' ? 'cap' : order?.column],
        [order?.direction]
      ),
    [preparedItems, order?.column, order?.direction]
  )

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  const columns = useMemo(
    () =>
      createColumns(
        partners,
        session,
        costErrorNumber,
        capErrorNumber,
        handleCapChange,
        readOnly,
        contentIsReadOnly,
        allOption,
        publicationFilters,
        t
      ),
    [
      partners,
      session,
      costErrorNumber,
      capErrorNumber,
      handleCapChange,
      readOnly,
      contentIsReadOnly,
      allOption,
      publicationFilters,
      t,
    ]
  )

  const blindPositiveCarsWithAtLeastOneOffer = getFirstDefinedSessionValue(
    session,
    BLIND_POSITIVE_COUNTER_FIELDS
  )

  const blindNegativeCarsWithAtLeastOneOffer = getFirstDefinedSessionValue(
    session,
    BLIND_NEGATIVE_COUNTER_FIELDS
  )

  const hasBlindSplitRecapCounters =
    blindPositiveCarsWithAtLeastOneOffer !== undefined ||
    blindNegativeCarsWithAtLeastOneOffer !== undefined

  const blindRecapCounters = hasBlindSplitRecapCounters
    ? [
        {
          label: t('assignment.publication.atLeastOneBlindPositiveOffer', {
            defaultValue: 'Auto che hanno ricevuto almeno 1 offerta Blind+',
          }),
          value: blindPositiveCarsWithAtLeastOneOffer ?? 0,
        },
        {
          label: t('assignment.publication.atLeastOneBlindNegativeOffer', {
            defaultValue: 'Auto che hanno ricevuto almeno 1 offerta Blind-',
          }),
          value: blindNegativeCarsWithAtLeastOneOffer ?? 0,
        },
      ]
    : [
        {
          label: t('assignment.publication.atLeastOneBlindOffer'),
          value:
            getFirstDefinedSessionValue(session, BLIND_LEGACY_COUNTER_FIELDS) ??
            0,
        },
      ]

  return (
    <div data-testid="assignment-publication">
      <TitleBar
        title={t('assignment.publication.contentTitle')}
        description={t('assignment.publication.contentDescription')}
        rightColumn={
          <>
            <ButtonLink
              color="textPrimary"
              underline="none"
              data-testid="button-gotoplanning"
              onClick={() =>
                navigate(
                  getSessionsStepUrl(session.sessionId, SESSIONS_STEP_PLANNING)
                )
              }
              disabled={
                !!session.assignmentStartedAt || readOnly || contentIsReadOnly
              }
            >
              <strong>{t('assignment.publication.extendPublication')}</strong>
            </ButtonLink>
            <Box pr={2} />
            <Button
              color="primary"
              variant="contained"
              data-testid="button-confirm"
              disabled={
                !!costErrorNumber ||
                !!capErrorNumber ||
                !handleValidation() ||
                readOnly ||
                contentIsReadOnly
              }
              onClick={handleOpenConfirmationDialog}
              sx={{ marginLeft: (theme) => theme.spacing(2) }}
            >
              {t('assignment.publication.closePublication')}
            </Button>
          </>
        }
      />
      <GridWrapper>
        <div>
          <Typography variant="body2" color="textSecondary">
            {t('assignment.publication.atLeastOneCar')}
          </Typography>
          <Typography variant="body2" color="primary">
            <strong>{session.partnerWithAtLeasOneChoice}</strong>
          </Typography>
        </div>
        <div>
          <Typography variant="body2" color="textSecondary">
            {t('assignment.publication.atLeastOnePreference')}
          </Typography>
          <Typography variant="body2" color="primary">
            <strong>{session.carsWithAtLeasOneChoice}</strong>
          </Typography>
        </div>
        {blindRecapCounters.map(({ label, value }) => (
          <div key={label}>
            <Typography variant="body2" color="textSecondary">
              {label}
            </Typography>
            <Typography variant="body2" color="primary">
              <strong>{value}</strong>
            </Typography>
          </div>
        ))}
      </GridWrapper>
      <Divider />
      <Box pb={3} />
      <Typography variant="body1" color="textPrimary">
        <strong>{t('assignment.publication.partnerCostingRecap')}</strong>
      </Typography>
      <Box pb={2} />

      {isFetchingPartners ? (
        <PageLoading />
      ) : (
        <Table
          id="assignment-caps-list"
          items={sortedPartners}
          columns={columns}
          isFetching={isFetchingPartners}
          sortable
          orderColumn={order?.column}
          orderDirection={order?.direction}
          handleSort={setSort}
        />
      )}
      <ConfirmationDialog
        title={t('assignment.publication.confirmationDialog.title')}
        content={t('assignment.publication.confirmationDialog.content')}
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
      />
    </div>
  )
}

AssignmentPublication.propTypes = {
  session: T.object.isRequired,
  partners: T.array,
  handleValidation: T.func.isRequired,
  handleStartAssignment: T.func,
  handleCapChange: T.func,
  isFetchingPartners: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
